// PRD.275 — Elia AI Assistant Drawer. PRD.278 follow-up — Change scope selector.
//
// Human-readable patient/visit context banner (FR-6). Props-driven. The Change
// affordance opens a scope selector: "Current patient" or one of the patient's
// visits. Selecting sets a manual override on the effective scope (frontend
// only — it never mutates real patient/encounter state).

import { useEffect, useRef, useState } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';

type VisitOption = { id: string; visitDate: string };

export type EliaContextBannerProps = {
  patientName?: string;
  contextLabel?: string;
  /** Active visit id when in visit scope (highlights the selector). */
  activeVisitId?: string;
  availableVisits?: VisitOption[];
  onSelectPatient?: () => void;
  onSelectVisit?: (visitId: string, visitDate?: string) => void;
};

export function EliaContextBanner({
  patientName,
  contextLabel,
  activeVisitId,
  availableVisits = [],
  onSelectPatient,
  onSelectVisit,
}: EliaContextBannerProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  if (!patientName) return null;
  const canChange = !!onSelectPatient;

  return (
    <div style={{ padding: '8px 12px 4px' }}>
      <div
        style={{
          background: ELIA_COLORS.pinkSoft,
          borderRadius: 14,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          fontFamily: ELIA_FONT,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 600,
              color: ELIA_COLORS.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Discussing {patientName}
          </p>
          {contextLabel ? (
            <p
              style={{
                margin: 0,
                fontSize: 10,
                color: ELIA_COLORS.textMuted,
                whiteSpace: 'nowrap',
              }}
            >
              {contextLabel}
            </p>
          ) : null}
        </div>

        {canChange ? (
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                minHeight: 24,
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                padding: '4px 0',
                cursor: 'pointer',
                color: ELIA_COLORS.pink,
                fontFamily: ELIA_FONT,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              Change
            </button>
            {menuOpen ? (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  minWidth: 200,
                  maxHeight: 260,
                  overflowY: 'auto',
                  background: ELIA_COLORS.surface,
                  border: `1px solid ${ELIA_COLORS.border}`,
                  borderRadius: 10,
                  boxShadow: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.1)',
                  padding: 4,
                  zIndex: 1,
                }}
              >
                <ScopeMenuItem
                  label="Current patient"
                  active={!activeVisitId}
                  onClick={() => {
                    onSelectPatient?.();
                    setMenuOpen(false);
                  }}
                />
                {availableVisits.map((v) => (
                  <ScopeMenuItem
                    key={v.id}
                    label={`Visit · ${v.visitDate}`}
                    active={v.id === activeVisitId}
                    onClick={() => {
                      onSelectVisit?.(v.id, v.visitDate);
                      setMenuOpen(false);
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ScopeMenuItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: active ? ELIA_COLORS.pinkSoft : 'transparent',
        border: 'none',
        borderRadius: 8,
        padding: '8px 12px',
        fontFamily: ELIA_FONT,
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        color: ELIA_COLORS.text,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
